/**
 * Environment Configuration Context
 * 
 * Provides environment-specific settings across the app:
 * - Environment detection (development, staging, production)
 * - Feature flags
 * - Analytics control
 * - Site access mode (open, authenticated_only, read_only, maintenance)
 */

import { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';
import { BACKEND_URL } from "../config";
const defaultConfig = {
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  isStaging: false,
  isProduction: false,
  isDevelopment: true,
  features: {
    analyticsEnabled: false,
    showEnvironmentBadge: true,
    tawkMode: "hidden", // "hidden" | "all_pages" | "contacts_only"
  },
  siteAccess: {
    mode: 'open',
    mode_config: {
      allows_anonymous: true,
      allows_write: true,
      shows_maintenance: false,
    },
    maintenance_message: '',
    read_only_message: '',
  },
  registrationMode: 'open',
  approvalMode: 'automatic',
  recaptchaEnabled: false,
  loaded: false,
};

const ConfigContext = createContext(defaultConfig);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Fetch all configs in parallel (including security for recaptcha)
        const [configRes, siteAccessRes, registrationRes, securityRes] = await Promise.all([
          api.get("/api/config/public"),
          api.get("/api/settings/site-access"),
          api.get("/api/settings/registration").catch(() => ({ data: { mode: 'open' } })),
          api.get("/api/settings/security").catch(() => ({ data: { recaptcha_enabled: false } })),
        ]);
        
        const data = configRes.data;
        const siteAccess = siteAccessRes.data;
        const registration = registrationRes.data;
        const security = securityRes.data;
        
        setConfig({
          environment: data.environment,
          isStaging: data.is_staging,
          isProduction: data.is_production,
          isDevelopment: data.environment === 'development',
          features: {
            analyticsEnabled: data.features?.analytics_enabled ?? false,
            showEnvironmentBadge: data.features?.show_environment_badge ?? false,
            tawkEnabled: data.features?.tawk_enabled ?? false,
          },
          siteAccess: {
            mode: siteAccess.mode || 'open',
            mode_config: siteAccess.mode_config || defaultConfig.siteAccess.mode_config,
            maintenance_message: siteAccess.maintenance_message || '',
            read_only_message: siteAccess.read_only_message || '',
          },
          registrationMode: registration.mode || 'open',
          approvalMode: registration.approval_mode || 'automatic',
          recaptchaEnabled: security.recaptcha_enabled ?? false,
          loaded: true,
        });
      } catch (error) {
        // Use defaults based on hostname as fallback
        const hostname = window.location.hostname;
        const isStaging = hostname.includes('stg.') || hostname.includes('staging');
        const isProduction = hostname === 'mototrips.net' || hostname === 'www.mototrips.net';
        
        setConfig({
          ...defaultConfig,
          isStaging,
          isProduction,
          isDevelopment: !isStaging && !isProduction,
          features: {
            analyticsEnabled: isProduction,
            showEnvironmentBadge: isStaging || (!isProduction && !isStaging),
            tawkMode: isProduction ? "all_pages" : "hidden",
          },
          loaded: true,
        });
      }
    };

    fetchConfig();
  }, []);

  // Function to refresh site access settings (useful after admin changes)
  const refreshSiteAccess = async () => {
    try {
      const res = await api.get("/api/settings/site-access");
      setConfig(prev => ({
        ...prev,
        siteAccess: {
          mode: res.data.mode || 'open',
          mode_config: res.data.mode_config || defaultConfig.siteAccess.mode_config,
          maintenance_message: res.data.maintenance_message || '',
          read_only_message: res.data.read_only_message || '',
        },
      }));
    } catch (error) {
    }
  };

  // Function to refresh registration mode (useful after admin changes)
  const refreshRegistrationMode = async () => {
    try {
      const res = await api.get("/api/settings/registration");
      setConfig(prev => ({
        ...prev,
        registrationMode: res.data.mode || 'open',
        approvalMode: res.data.approval_mode || 'automatic',
      }));
    } catch (error) {
    }
  };

  return (
    <ConfigContext.Provider value={{ ...config, refreshSiteAccess, refreshRegistrationMode }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export default ConfigContext;
